/* eslint-disable @typescript-eslint/no-empty-function */

import { useAdminUser } from '@orientaction/hooks';
import { createContext, ReactNode, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import usePersistedState from '../hooks/usePersistedState'; // Importez le hook

interface INavigationState {
  search: string;
  page: number;
  tab: number;
}

interface INavigationContextType extends INavigationState {
  setPage: (page: number) => void;
  setSearchTerm: (term: string) => void;
  setTab: (tab: number) => void;
}

const initialState: INavigationState = {
  search: '',
  page: 0,
  tab: 1,
};

const KEEP_ADMIN_NAV_PATHS = [
  'admin/users-resultats',
  'admin/resultat-beneficiaire/',
  'admin/ressources-beneficiaire/',
  '/boussole/',
  'mes-resultats/',
  'resultat-',
  'res-pref-j',
  'game/result',
  'ebook-intro',
  'ebook-etape',
  'ebook-recrutement',
  '/ebook/',
];

const shouldKeepAdminNavState = (pathname: string) =>
  KEEP_ADMIN_NAV_PATHS.some(fragment => pathname.includes(fragment));

const initialContextValue: INavigationContextType = {
  ...initialState,
  setPage: () => {},
  setSearchTerm: () => {},
  setTab: () => {}, //
};

const NavigationContext = createContext<INavigationContextType>(initialContextValue);

export const NavigationProvider = ({ children }: { children: ReactNode }) => {
  const [navState, setNavState] = usePersistedState('app_nav_state', initialState);
  const { pathname } = useLocation();
  const { setTextFilter } = useAdminUser();
  useEffect(() => {
    if (shouldKeepAdminNavState(pathname)) {
      try {
        window.sessionStorage.setItem('app_nav_state', JSON.stringify(navState));
      } catch (error) {
        console.error('Erreur lors de la sauvegarde :', error);
      }
    } else {
      setNavState(initialState);
      setTextFilter('');
      window.sessionStorage.setItem('app_nav_state', JSON.stringify(initialState));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, navState, setNavState]);

  const setPage = (page: number) => {
    setNavState((prev: INavigationState) => ({ ...prev, page }));
  };

  const setSearchTerm = (term: string) => {
    setNavState((prev: INavigationState) => ({ ...prev, search: term, page: 1 }));
  };

  const setTab = (tab: number) => {
    setNavState((prev: INavigationState) => ({ ...prev, tab }));
  };

  const contextValue: INavigationContextType = {
    ...navState,
    setPage,
    setSearchTerm,
    setTab,
  };

  return <NavigationContext.Provider value={contextValue}>{children}</NavigationContext.Provider>;
};

export const useNavigation = () => useContext(NavigationContext);
